import { traverseObject } from '@/shared/parse';
import { MarkdownService } from './MarkdownService';
import { FunctionFactory, Model, Serializer, slk } from 'survey-core';
import { runtimeConfig } from './RuntimeConfig';

const STEP3_ACTION_PLAN_MODULES = ['08', '09', '10', '11'];

export class SurveyService {

  static configure() {

    slk("MDkzYmU1ODItODFiMi00N2U4LWIzMzUtNzMwNmE3ODQ3ZjQxOzE9MjAyNi0wMS0xNCwyPTIwMjYtMDEtMTQsND0yMDI2LTAxLTE0");

    FunctionFactory.Instance.register("ISILOVISIBLE", SurveyService.ISILOVISIBLE);
    FunctionFactory.Instance.register("ILOCHECK", SurveyService.ILOCHECK);
    FunctionFactory.Instance.register("UNCOVERED_ILOS", SurveyService.UNCOVERED_ILOS);
    FunctionFactory.Instance.register("GET_ASSESSMENT_WEIGHTING_SUM", SurveyService.GET_ASSESSMENT_WEIGHTING_SUM);

    // add property for file
    Serializer.addProperty("file", {
      name: "summaryLabel:string",
      category: "general",
      type: "string",
      visibleIndex: 2,
    });

    Serializer.addProperty("paneldynamic", {
      name: "minPanelCountExpression:expression",
      category: "general",
      type: "expression",
      visibleIndex: 15,
      onExecuteExpression: (obj, res) => {
        if (res !== undefined) {
          obj.minPanelCount = res;
        }
      }
    });

    Serializer.addProperty("paneldynamic", {
      name: "maxPanelCountExpression:expression",
      category: "general",
      type: "expression",
      visibleIndex: 17,
      onExecuteExpression: (obj, res) => {
        if (res !== undefined) {
          obj.maxPanelCount = res;
        }
      }
    });
  }

  static preprocessResults(survey: Model, options: any) {

    if (options.name.startsWith('ActionPlan')) {

      if (options.name === 'ActionPlan') {

        for (const key of STEP3_ACTION_PLAN_MODULES) {
          const oldValue = survey.getValue(`ActionPlan${key}`) ?? [];
          const newValue = options?.value?.filter((x: any) => x.Area === `Area${key}`).map((x: any) => { delete x.Area; return x; }) ?? [];
          if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
            survey.setValue(`ActionPlan${key}`, newValue, false, false);
          }
        }

      } else {
        const oldActionList: any[] = survey.getValue('ActionPlan') ?? [];
        const newActionList = STEP3_ACTION_PLAN_MODULES.reduce((acc: any[], key: string) => {
          const moduleActionList = survey.getValue(`ActionPlan${key}`) ?? [];
          return moduleActionList ? [...acc, ...moduleActionList.map((x: any) => ({ Area: `Area${key}`, ...x, }))] : acc;
        }, []);

        if (JSON.stringify(oldActionList) !== JSON.stringify(newActionList)) {
          survey.setValue('ActionPlan', newActionList, false, false);
        }
      }
    }

    // TODO: remove this when the survey is fixed
    if (options.name === 'CourseMapping') {
      survey.getQuestionByName('CourseMapping').isAnswered = !!options?.value;
    }

  }

  public static getAnsweredQuestionCount(questionNames: string[], results: any) {
    let answeredQuestionCount = 0;
      for (const questionName of questionNames) {
        let found = false;
      traverseObject(results, (obj: any) => {
        if (obj?.[questionName]) {
          found = true;
        }
      });
      if (found) {
        ++answeredQuestionCount;
      }
    }
    return answeredQuestionCount;
  }

  public static getPageTitleFromInfo(info: { title: string, questionCount: number, answeredQuestionCount: number }) {
    return info?.questionCount > 0
      ? (info?.answeredQuestionCount < info?.questionCount
        ? `${info?.title}${String.fromCharCode(160)}(${info.answeredQuestionCount}/${info.questionCount})`
        : `${info?.title}${String.fromCharCode(160)}✓`)
      : info.title;
  }

  public static getPageTitle(survey: Model, pageName: string): string {
    const info = SurveyService.getPageStatus(survey, pageName);
    return this.getPageTitleFromInfo(info);
  }

  public static getPageStatus(survey: Model, pageName: string) {
    const results = survey.data;
    const page = survey.getPageByName(pageName);
    const [title] = (page.title as string).split(/\(|✓/);
    const questions = page.getQuestions(true);
    const questionNames = questions.filter(q => q.isRequired && q.isVisibleInSurvey).map(q => q.name);
    const questionCount = questionNames.length;
    const answeredQuestionCount = questionNames.length && this.getAnsweredQuestionCount(questionNames, results) || 0;
    return {
      title,
      questionCount,
      answeredQuestionCount
    };
  }

  public static processMarkdown = (str: string) => {

    //convert the markdown text to html
    str = str.replace(/^(\d+)\./, '$1\\.');
    str = str.replace(/^(\d+)\)/, '$1\\)');
    str = MarkdownService.render(str);

    return str;
  }


  public static ISILOVISIBLE(params: any[]) {
    const outcomes: { ILOID: string, ILO: string, OLO: string }[] = params[0];
    const olo: string = params[1];
    const ilo: string = params[2];

    const CourseGeneralSkills = params[3] as string[];
    const CourseChallengeAreas = params[4] as string[];

    const self = this as any;
    const row = self.row;
    if (!CourseGeneralSkills?.includes(row.name) && !CourseChallengeAreas?.includes(row.name) && row.name !== 'ILO') {
      return false;
    }

    return Array.isArray(outcomes) && outcomes.some(x => x.ILOID === ilo && x.OLO === olo);
  }

  public static ILOCHECK(params: any[]) {
    const olos: string[] = params[0];
    const ilos: { ILOID: string, ILO: string, OLO: string }[] = params[1];
    const count: number = params[2];

    return Array.isArray(olos) && Array.isArray(ilos) && olos.every(olo => ilos.reduce((acc, ilo) => acc + (ilo.OLO === olo ? 1 : 0), 0) >= count);
  }

  public static UNCOVERED_ILOS(params: any[]) {
    const ilos: { ILOID: string, ILO: string }[] = params[0] || [];
    const assessmentPlan: { AssessedILOs: string[] }[] = params[1] || [];

    // Get all assessed ILO IDs from summative assessments
    const assessedILOIds = Array.isArray(assessmentPlan) 
      ? assessmentPlan.flatMap(assessment => Array.isArray(assessment.AssessedILOs) ? assessment.AssessedILOs : [])
      : [];

    return ilos
      .filter(ilo => !assessedILOIds.includes(ilo.ILOID))
      .map(ilo => `${ilo.ILOID}: ${ilo.ILO}`)
      .join(', ');
  }

  public static GET_ASSESSMENT_WEIGHTING_SUM(params: any[]) {
    const assessmentPlan: { AssessmentMethods: string, AssessmentWeighting: number }[] = params[0] || [];

    // Filter only summative assessments and sum their weightings
    const summativeAssessments = Array.isArray(assessmentPlan) 
      ? assessmentPlan.filter(assessment => assessment.AssessmentMethods === 'Summative_Assessment')
      : [];

    // If there are no summative assessments, return 0
    if (summativeAssessments.length === 0) {
      return 0;
    }

    // Sum the weightings of summative assessments
    const totalWeight = summativeAssessments.reduce((sum, assessment) => {
      const weight = Number(assessment.AssessmentWeighting) || 0;
      return sum + weight;
    }, 0);

    // Return the calculated sum
    return Math.round(totalWeight * 100) / 100; // Round to 2 decimal places
  }

  public static preprocessTemplate(survey: Model, moduleId: string, action?: string, showMarkup?: boolean) {

    const pages = survey.pages;

    const results = survey.data || {};

    const haveApprovalData =
      !!results[`Approval${moduleId}`] ||
      !!results[`FeedbackReject${moduleId}`] ||
      !!results[`FeedbackApprove${moduleId}`];

    // const haveExpertData = 
    //   !!results[`AwardRecommended`];

    for (const page of pages) {

      const isApprovalPage = page.name === `Approval`;
      const isExpertPage = page.name === `Expert`;

      switch (action) {

        case 'edit':
          page.visible = (!isApprovalPage || (isApprovalPage && haveApprovalData));
          page.readOnly = isApprovalPage;
          break;

        case 'view':
          page.visible = (!isApprovalPage || (isApprovalPage && haveApprovalData));
          page.readOnly = true;
          break;

        case 'review':
          page.visible = !isExpertPage;
          page.readOnly = !isApprovalPage;
          break;
      }

      if (showMarkup) {
        page.title = this.getPageTitle(survey, page.name);
      }

    }
  }

  public static openSharePoint(relativeUrl: string) {
    let url = `${runtimeConfig.spSiteUrl}/${relativeUrl}`;
    window.open(url, relativeUrl);
  }

}